// src/rms/constants/env.js
// Environment-driven configuration with safe fallbacks for RMS
/**
 * RMS env mapping
 * ---------------
 * What:
 *   Host -> rmsOrgId (Firestore document ID in rms_organisations) mapping
 *   and a default org ID.
 *
 * Why:
 *   - Public RMS routes are unauthenticated; we must infer the tenant (org)
 *     early to fetch the correct schema and render per-org copy.
 *
 * How to configure:
 *   - Set VITE_RMS_ORG_MAP to a JSON object, e.g.:
 *       {"localhost:3000":"RMS-ORG-00001","<your-staging-host>.web.app":"RMS-ORG-00001"}
 *   - Set VITE_RMS_DEFAULT_ORG_ID to a valid rms_organisations/{rmsOrgId} doc ID.
 *
 * Safety:
 *   - Values here must be rms_organisations Firestore document IDs (RMS-ORG-XXXXX format).
 *   - Defaults cover localhost and hosted web.app domains for convenience.
 */

// Default RMS organisation (rms_organisations doc ID) when no mapping is found
export const RMS_DEFAULT_ORG_ID = import.meta.env.VITE_RMS_DEFAULT_ORG_ID || 'RMS-ORG-00001';

// Optional JSON mapping: host(or host:port) -> rmsOrgId (rms_organisations doc ID)
// Example:
// VITE_RMS_ORG_MAP='{"localhost:3000":"RMS-ORG-00001","<your-staging-host>.web.app":"RMS-ORG-00001"}'
function parseOrgMap(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
    return null;
  } catch (e) {
    console.warn('[RMS] Failed to parse VITE_RMS_ORG_MAP; falling back to defaults');
    return null;
  }
}

const envMap = parseOrgMap(import.meta.env.VITE_RMS_ORG_MAP);

// Safe default mapping for local dev and common hosted environments
// Values are rms_organisations Firestore document IDs (RMS-ORG-XXXXX format).
// Important: we avoid including staging hostnames in production bundles to prevent
// deployment verification from flagging a false positive. Because CRA replaces
// process.env.* at build time and performs dead-code elimination, this conditional
// ensures the staging string is not present in the production build.
const isProdBuild = (import.meta.env.VITE_ENVIRONMENT === 'production' || process.env.NODE_ENV === 'production');
// Derive a potential staging host dynamically from Firebase project id when available.
// This avoids hardcoding any specific staging hostname in source (which would appear in sourcemaps).
const derivedHost = (import.meta.env.VITE_FIREBASE_PROJECT_ID ? `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.web.app` : null);
const defaultMap = (() => {
  if (isProdBuild) {
    return {
      'think-divergent-portal.web.app': 'RMS-ORG-00001',
      'think-divergent-lcs-nhs.web.app': 'RMS-ORG-00002',
    };
  }
  const map = {
    'localhost:3000': 'RMS-ORG-00001',
    'think-divergent-portal.web.app': 'RMS-ORG-00001',
    'think-divergent-lcs-nhs.web.app': 'RMS-ORG-00002',
  };
  if (derivedHost) {
    map[derivedHost] = 'RMS-ORG-00001';
  }
  return map;
})();

export const RMS_ORG_MAP = envMap || defaultMap;


